import { computePosition, flip, shift, offset, arrow } from "@floating-ui/dom";

/**
 * Example usage:
 * <tooltip-item tooltip-id="tooltip-id">
 *     <button aria-expanded="false" data-tooltip-trigger="tooltip-id" aria-describedby="tooltip-id">Hover me</button>
 *     <div id="tooltip-id" class="tooltip-item__tooltip" role="tooltip">This is a tooltip</div>
 * </tooltip-item>
 */
class TooltipItem extends HTMLElement {
    constructor() {
        super();

        this.tooltip = this.querySelector(`[role="tooltip"]`);
        this.tooltipArrow = this.tooltip.querySelector(".tooltip-item__arrow");
        this.trigger = this.querySelector(`[data-tooltip-trigger]`);

        this.init();
    }

    init() {
        this.addEventListeners();
    }

    addEventListeners() {
        [
            ["mouseenter", this.showTooltip.bind(this)],
            ["mouseleave", this.hideTooltip.bind(this)],
            ["focus", this.showTooltip.bind(this)],
            ["blur", this.hideTooltip.bind(this)],
            ["keydown", this.handleKeydown.bind(this)],
        ].forEach(([event, listener]) => {
            this.trigger.addEventListener(event, listener);
        });
    }

    updateTooltip() {
        computePosition(this.trigger, this.tooltip, {
            placement: "top",
            middleware: [
                offset(9),
                flip(),
                shift({ padding: 5, crossAxis: true }),
                arrow({ element: this.tooltipArrow }),
            ],
        }).then(({ x, y, placement, middlewareData }) => {
            Object.assign(this.tooltip.style, {
                left: `${x}px`,
                top: `${y}px`,
            });

            // Accessing the data
            const { x: arrowX, y: arrowY } = middlewareData.arrow;

            const staticSide = {
                top: "bottom",
                right: "left",
                bottom: "top",
                left: "right",
            }[placement.split("-")[0]];

            Object.assign(this.tooltipArrow.style, {
                left: arrowX != null ? `${arrowX}px` : "",
                top: arrowY != null ? `${arrowY}px` : "",
                right: "",
                bottom: "",
                [staticSide]: "-4px",
            });
        });
    }

    showTooltip() {
        this.tooltip.hidden = false;
        this.trigger.setAttribute("aria-expanded", "true");
        this.updateTooltip();
    }

    hideTooltip() {
        this.tooltip.hidden = true;
        this.trigger.setAttribute("aria-expanded", "false");
    }

    handleKeydown(event) {
        if (event.key === "Escape") {
            this.hideTooltip();
        }
    }
}

customElements.define("tooltip-item", TooltipItem);
